/**
 * @format
 */

import axios from 'axios';
import { SignInCredentials, SignInResponse } from '../types/auth.types';

const apiUrl = process.env.REACT_APP_API_URL;

class AuthService {
  async signIn(credentials: SignInCredentials): Promise<SignInResponse> {
    try {
      const response = await axios.post<SignInResponse>(
        `${apiUrl}/auth/login`,
        credentials,
        { withCredentials: true }
      );
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error;
    }
  }

  /**
   * Initiates a password reset for the specified username by sending a reset request to the backend.
   * @param {string} username - The username of the user requesting a password reset.
   * @returns {Promise<any>} The response data from the backend, typically including reset instructions.
   * @throws {Error} Throws an error if the password reset initiation fails.
   */
  static async initiateReset(username: string) {
    try {
      const response = await axios.post(`${apiUrl}/auth/password-reset/request`, {
        username,
      });
      return response.data;
    } catch (error: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = error as any;
      
      if (e.response && e.response.data) {
        // Extract error message from response
        const errorMessage = e.response.data?.message || e.response.data?.error || 'Password reset initiation failed';
        throw new Error(errorMessage);
      } else if (error instanceof Error) {
        throw new Error(error.message);
      } else {
        throw new Error('An unknown error occurred during password reset initiation');
      }
    }
  }

  /**
   * Confirms a password reset by providing the username, confirmation code, and new password.
   * @param {string} username - The username of the user resetting their password.
   * @param {string} confirmationCode - The confirmation code provided to the user.
   * @param {string} newPassword - The new password the user wishes to set.
   * @returns {Promise<any>} The response data from the backend, typically confirming the reset.
   * @throws {Error} Throws an error if the password reset confirmation fails.
   */
  static async confirmReset(
    username: string,
    confirmationCode: string,
    newPassword: string
  ) {
    try {
      const response = await axios.post(`${apiUrl}/auth/password-reset/confirm`, {
        username,
        confirmationCode,
        newPassword,
      });
      return response.data;
    } catch (error: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = error as any;
      
      if (e.response && e.response.data) {
        // Extract error message from response
        const errorMessage = e.response.data?.message || e.response.data?.error || 'Password reset confirmation failed';
        throw new Error(errorMessage);
      } else if (error instanceof Error) {
        throw new Error(error.message);
      } else {
        throw new Error('An unknown error occurred during password reset confirmation');
      }
    }
  }

  /**
   * Updates password for NEW_PASSWORD_REQUIRED challenge during sign-in.
   * @param {string} session - The session token from the NEW_PASSWORD_REQUIRED challenge.
   * @param {string} username - The username of the user updating their password.
   * @param {string} newPassword - The new password the user wishes to set.
   * @returns {Promise<SignInResponse>} The response data from the backend with tokens.
   * @throws {Error} Throws an error if the password update fails.
   */
  static async updatePassword(
    session: string,
    username: string,
    newPassword: string
  ): Promise<SignInResponse> {
    try {
      const response = await axios.post(`${apiUrl}/auth/update-password`, {
        session,
        username,
        newPassword,
      });

      const data = response.data as SignInResponse;

      // Check if the response indicates failure
      if (data.success === false) {
        const serverMsg = data.error ?? data.message ?? "Password update failed";
        throw new Error(typeof serverMsg === 'string' ? serverMsg : JSON.stringify(serverMsg));
      }

      return data;
    } catch (error: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = error as any;
      
      if (e.response && e.response.data) {
        const errorData = e.response.data as SignInResponse;
        const serverMsg = errorData.error ?? errorData.message ?? "Password update failed";
        throw new Error(typeof serverMsg === 'string' ? serverMsg : JSON.stringify(serverMsg));
      } else if (error instanceof Error) {
        throw new Error(error.message);
      } else {
        throw new Error('An unknown error occurred during password update');
      }
    }
  }

}

export const authService = new AuthService();
export { AuthService };
